import { useCallback } from 'react';
import { Button } from '@/components/common';
import { NewOperationButton } from '@/features/operations';
import { useReviewModalStore } from '@/store/reviewModalStore';
import { useMonitoring } from './useMonitoring';
import { InstructionList } from './InstructionList';
import { ReviewModal } from './review-modal';
import type { Instruction } from './types';

export function MonitoringPage() {
  const { data, pagination, isLoading, error, refresh, isRefreshing } = useMonitoring();

  const openReviewModal = useReviewModalStore((s) => s.open);
  const closeModal = useReviewModalStore((s) => s.close);

  const handleRowClick = useCallback(
    (instruction: Instruction) => {
      openReviewModal(instruction.payment_id);
    },
    [openReviewModal],
  );

  const handleCloseModal = useCallback(() => {
    closeModal();
    refresh();
  }, [closeModal, refresh]);

  return (
    <div className="space-y-6">
      {/* Header — matches legacy TM header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold text-[#1a1a1a]">Instruction List</h1>
          <p className="text-[13px] uppercase tracking-[2px] text-[#999]">Transaction Monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <NewOperationButton size="sm" />
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={isRefreshing}
            aria-label="Refresh instructions"
          >
            {isRefreshing ? 'Refreshing\u2026' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div
          className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
          role="alert"
        >
          <span className="font-semibold">Failed to load instructions.</span> Please try again.
          <button
            onClick={refresh}
            className="ml-3 text-xs font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Instruction table with filters */}
      <InstructionList
        data={data}
        pagination={pagination}
        isLoading={isLoading}
        onRowClick={handleRowClick}
      />

      {/* Review Modal (Module 7.10) — always mounted so Radix Dialog can animate exit */}
      <ReviewModal onClose={handleCloseModal} />
    </div>
  );
}
